import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { createReactBlockSpec } from "@blocknote/react";
import { useDashboards, useDashboard } from "@/api/dashboard";
import { ResizeHandle } from "./resize-handle";
import { Select, Typography } from "@/components/ui";
import useTranslation from "@/hooks/use-translation";
import { FaChartBar, FaLayerGroup, FaExternalLinkAlt } from "react-icons/fa";
import { Link } from "react-router";
import { extractDashboardData } from "@/utils/dashboard-converter";
import { DashboardPopulated } from "@/models/dashboard";
import { getBackendWidgetType } from "@/models/widget-v2";
import { useTextWidgetFilters } from "./text-widget-context";

// Lazy import du renderer pour éviter les problèmes de rendu
const TemplateWidgetRenderer = React.lazy(
  () => import("./WidgetRendererWrapper"),
);

// Constantes pour le rendu du layout
// Basé sur une grille de 12 colonnes comme react-grid-layout
const GRID_COLS = 12;
const ROW_HEIGHT = 80; // Hauteur d'une unité de grille
const MARGIN = 10; // Espacement entre widgets
const PADDING = 10; // Padding autour du layout
// Largeur de rendu (assez grande pour un rendu net)
const RENDER_WIDTH = 1000;

// Composant de fallback pendant le chargement
const DashboardLoadingFallback: React.FC<{ height: number }> = ({ height }) => (
  <div
    style={{
      height,
      width: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "var(--arc-surface-secondary)",
      borderRadius: "8px",
    }}
  >
    <span style={{ color: "var(--arc-content-tertiary)" }}>
      Chargement du dashboard...
    </span>
  </div>
);

// Composant de sélection de dashboard
interface DashboardSelectorProps {
  onSelect: (dashboardId: string) => void;
  height: number;
}

const DashboardSelector: React.FC<DashboardSelectorProps> = ({
  onSelect,
  height,
}) => {
  const { data: dashboards, isLoading } = useDashboards();

  const options = useMemo(
    () =>
      dashboards?.map((d) => ({
        value: d.id || "",
        label: String(d.name || d.metadata?.name || "Dashboard sans nom"),
      })) || [],
    [dashboards],
  );

  if (isLoading) {
    return <DashboardLoadingFallback height={height} />;
  }

  return (
    <div
      style={{
        height,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "var(--arc-surface-secondary)",
        borderRadius: "8px",
        padding: "24px",
        gap: "16px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          color: "var(--arc-content-secondary)",
        }}
      >
        <FaChartBar style={{ fontSize: "24px" }} />
        <Typography variant="h6">Sélectionnez un dashboard</Typography>
      </div>

      <div style={{ width: "100%", maxWidth: "300px" }}>
        <Select
          options={options}
          placeholder="Choisir un dashboard..."
          onChange={(value) => onSelect(value)}
          size="md"
        />
      </div>

      {options.length === 0 && (
        <Typography
          variant="text-sm"
          className="text-content-tertiary text-center"
        >
          Aucun dashboard disponible. Créez d'abord un dashboard.
        </Typography>
      )}
    </div>
  );
};

// Composant de preview du dashboard
interface DashboardPreviewProps {
  dashboardId: string;
  height: number;
}

const DashboardPreview: React.FC<DashboardPreviewProps> = ({
  dashboardId,
  height,
}) => {
  const { data: dashboard, isLoading, isError } = useDashboard(dashboardId);
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  // Utiliser le contexte de filtres du parent (fourni par TextWidget)
  const filtersCtx = useTextWidgetFilters();

  // Observer pour mesurer la largeur du container
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  // Widgets are now included in dashboard via fields=*.*
  const dashboardWidgets = dashboard?.widgets;

  // Extract widgets (already in BackendWidget format)
  const { widgets, layout: rawLayout } = useMemo(() => {
    if (dashboardWidgets && dashboardWidgets.length > 0) {
      return extractDashboardData(
        dashboardWidgets,
        dashboard as DashboardPopulated,
      );
    }
    return { widgets: {}, layout: [] };
  }, [dashboardWidgets, dashboard]);

  // Normaliser le layout pour occuper toute la largeur disponible
  const layout = useMemo(() => {
    if (rawLayout.length === 0) return rawLayout;

    // Trouver les bornes du layout
    const minX = Math.min(...rawLayout.map((item) => item.x));
    const maxX = Math.max(...rawLayout.map((item) => item.x + item.w));
    const usedCols = maxX - minX;

    // Si le layout utilise moins de colonnes, le normaliser pour utiliser les 12 colonnes
    if (usedCols < GRID_COLS && usedCols > 0) {
      const scale = GRID_COLS / usedCols;
      return rawLayout.map((item) => ({
        ...item,
        x: Math.round((item.x - minX) * scale),
        w: Math.round(item.w * scale),
      }));
    }

    // Sinon juste décaler pour commencer à 0
    if (minX > 0) {
      return rawLayout.map((item) => ({
        ...item,
        x: item.x - minX,
      }));
    }

    return rawLayout;
  }, [rawLayout]);

  // Calculer la largeur d'une cellule de grille (zone utile = RENDER_WIDTH - 2*PADDING)
  const gridWidth = RENDER_WIDTH - 2 * PADDING;
  const cellWidth = (gridWidth - (GRID_COLS - 1) * MARGIN) / GRID_COLS;

  // Calculer la hauteur du layout basée sur les positions réelles des widgets
  const renderHeight = useMemo(() => {
    if (layout.length === 0) return 300;
    const maxBottom = Math.max(...layout.map((item) => item.y + item.h));
    return maxBottom * (ROW_HEIGHT + MARGIN) + 2 * PADDING;
  }, [layout]);

  // Calculer le scale factor dynamiquement basé sur la largeur du container
  const scaleFactor = containerWidth > 0 ? containerWidth / RENDER_WIDTH : 0.5;
  const scaledHeight = renderHeight * scaleFactor;

  // Couleur du dashboard
  const dashboardColor = dashboard?.color || "var(--arc-accent)";

  if (isLoading) {
    return <DashboardLoadingFallback height={height} />;
  }

  if (isError || !dashboard) {
    return (
      <div
        style={{
          height,
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "var(--arc-surface-secondary)",
          borderRadius: "8px",
          color: "var(--color-error, #ef4444)",
        }}
      >
        <Typography variant="text-sm">
          Erreur lors du chargement du dashboard
        </Typography>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        borderRadius: "8px",
        overflow: "hidden",
        border: "1px solid var(--arc-border)",
        backgroundColor: "var(--arc-surface-elevated)",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Header avec nom du dashboard */}
      <div
        style={{
          padding: "12px 16px",
          borderBottom: "1px solid var(--arc-border)",
          background: `linear-gradient(135deg, ${dashboardColor}20 0%, ${dashboardColor}10 100%)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: `${dashboardColor}30`,
            }}
          >
            {dashboard?.icon ? (
              <span style={{ fontSize: "16px" }}>{dashboard.icon}</span>
            ) : (
              <FaChartBar style={{ fontSize: "14px", color: dashboardColor }} />
            )}
          </div>
          <div>
            <Typography
              variant="text-sm"
              className="font-semibold text-content-primary"
            >
              {String(
                dashboard?.name || dashboard?.metadata?.name || "Dashboard",
              )}
            </Typography>
            {typeof dashboard?.description === "string" && (
              <Typography variant="text-xs" className="text-content-secondary">
                {dashboard.description}
              </Typography>
            )}
          </div>
        </div>

        {/* Lien vers le dashboard */}
        <Link
          to={`/dashboards?id=${dashboardId}`}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            padding: "6px 12px",
            borderRadius: "6px",
            backgroundColor: isHovered ? "var(--arc-accent)" : "transparent",
            color: isHovered ? "white" : "var(--arc-content-secondary)",
            fontSize: "12px",
            fontWeight: 500,
            textDecoration: "none",
            transition: "all 0.2s ease",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <FaExternalLinkAlt style={{ fontSize: "10px" }} />
          Ouvrir
        </Link>
      </div>

      {/* Zone de preview avec miniature */}
      <div
        style={{
          position: "relative",
          overflow: "hidden",
          backgroundColor: "var(--arc-surface-secondary)",
          height: Math.max(scaledHeight, height - 60), // Au moins la hauteur demandée moins le header
        }}
      >
        {containerWidth > 0 ? (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: RENDER_WIDTH,
              height: renderHeight,
              transform: `scale(${scaleFactor})`,
              transformOrigin: "top left",
              pointerEvents: "none",
              backgroundColor: `${dashboardColor}08`,
            }}
          >
            {/* Widgets avec positionnement absolu basé sur le vrai layout */}
            {layout.map((item) => {
              const widgetData = widgets[item.i];
              if (!widgetData) return null;

              // Calculer la position et taille exactes (avec padding intégré)
              const left = PADDING + item.x * (cellWidth + MARGIN);
              const top = PADDING + item.y * (ROW_HEIGHT + MARGIN);
              const width = item.w * cellWidth + (item.w - 1) * MARGIN;
              const widgetHeight = item.h * ROW_HEIGHT + (item.h - 1) * MARGIN;

              return (
                <div
                  key={item.i}
                  style={{
                    position: "absolute",
                    left,
                    top,
                    width,
                    height: widgetHeight,
                    backgroundColor: "var(--arc-surface-elevated)",
                    borderRadius: "8px",
                    border: "1px solid var(--arc-border)",
                    overflow: "hidden",
                    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                  }}
                >
                  <div
                    style={{ height: "100%", width: "100%", padding: "8px" }}
                  >
                    <React.Suspense
                      fallback={
                        <div
                          style={{
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <span
                            style={{
                              color: "var(--arc-content-tertiary)",
                              fontSize: "12px",
                            }}
                          >
                            ...
                          </span>
                        </div>
                      }
                    >
                      <TemplateWidgetRenderer
                        widgetType={getBackendWidgetType(widgetData)}
                        widgetName={widgetData.name || "Widget"}
                        widgetConfig={widgetData}
                        filtersCtx={filtersCtx}
                      />
                    </React.Suspense>
                  </div>
                </div>
              );
            })}

            {/* Placeholder si pas de widgets */}
            {layout.length === 0 && (
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "var(--arc-content-tertiary)",
                }}
              >
                <FaLayerGroup
                  style={{
                    fontSize: "32px",
                    marginBottom: "8px",
                    opacity: 0.3,
                  }}
                />
                <span style={{ fontSize: "14px" }}>Aucun widget</span>
              </div>
            )}
          </div>
        ) : (
          <DashboardLoadingFallback height={height - 60} />
        )}
      </div>

      {/* Footer avec métadonnées */}
      <div
        style={{
          padding: "8px 16px",
          borderTop: "1px solid var(--arc-border)",
          backgroundColor: "var(--arc-surface)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontSize: "12px",
          color: "var(--arc-content-secondary)",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <FaLayerGroup style={{ color: "var(--arc-content-tertiary)" }} />
          {t("n_widgets", { count: layout.length })}
        </span>
      </div>
    </div>
  );
};

// Composant wrapper qui gère l'état du bloc dashboard
interface DashboardBlockContentProps {
  dashboardId: string;
  height: number;
  onDashboardSelect: (dashboardId: string) => void;
}

const DashboardBlockContent: React.FC<DashboardBlockContentProps> = ({
  dashboardId,
  height,
  onDashboardSelect,
}) => {
  const [isMounted, setIsMounted] = useState(false);

  // Attendre que le composant soit monté avant de rendre
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsMounted(true);
    }, 100);
    return () => {
      clearTimeout(timer);
      setIsMounted(false);
    };
  }, []);

  if (!isMounted) {
    return <DashboardLoadingFallback height={height} />;
  }

  // Si aucun dashboard sélectionné, afficher le sélecteur
  if (!dashboardId) {
    return <DashboardSelector onSelect={onDashboardSelect} height={height} />;
  }

  // Sinon afficher la preview du dashboard
  return <DashboardPreview dashboardId={dashboardId} height={height} />;
};

/**
 * Bloc personnalisé BlockNote pour afficher un dashboard inline dans l'éditeur de texte
 * createReactBlockSpec retourne une fonction qui doit être appelée avec () pour obtenir le BlockSpec
 */
export const DashboardBlock = createReactBlockSpec(
  {
    type: "dashboard",
    propSchema: {
      dashboardId: {
        default: "",
      },
      height: {
        default: 500,
      },
    },
    content: "none",
  },
  {
    render: (props) => {
      const { dashboardId, height } = props.block.props;
      const computedHeight = typeof height === "number" ? height : 400;

      // Handler pour mettre à jour le dashboardId
      const handleDashboardSelect = (newDashboardId: string) => {
        props.editor.updateBlock(props.block, {
          props: {
            ...props.block.props,
            dashboardId: newDashboardId,
          },
        });
      };

      // Handler pour mettre à jour la hauteur du bloc
      const handleResize = useCallback(
        (newHeight: number) => {
          props.editor.updateBlock(props.block, {
            props: {
              ...props.block.props,
              height: newHeight,
            },
          });
        },
        [props.editor, props.block],
      );

      return (
        <div
          contentEditable={false}
          className="group"
          onContextMenu={(e) => e.stopPropagation()}
          style={{
            position: "relative",
            width: "100%",
            borderRadius: "8px",
            overflow: "hidden",
          }}
        >
          <DashboardBlockContent
            dashboardId={dashboardId}
            height={computedHeight}
            onDashboardSelect={handleDashboardSelect}
          />
          <ResizeHandle
            currentHeight={computedHeight}
            minHeight={200}
            maxHeight={1000}
            onResize={handleResize}
          />
        </div>
      );
    },
  },
);

export default DashboardBlock;
